import React, { useEffect } from 'react'
import { StyleSheet } from 'react-native'
import { Iffy, DNABox, DNAButton, DNAText, useOrientationChange } from '@alucio/lux-ui'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import { DNAFolderGridList, useCurrentPage, DNABreadcrumb } from 'src/components/DNA'
import { Switch, Route } from 'src/router'
import { FolderORM, FolderItemORM, isDocumentVersionORM, DocumentORM } from 'src/types/types'
import useDisplayMode, { DisplayModes } from 'src/components/DNA/hooks/useDisplayMode'
import useFolderNavURL, { useFolderNavURLInit } from 'src/components/DNA/Folder/Nav/useFolderNavURL'
import { useDNAFolderActions } from 'src/components/DNA/Folder/DNAFolder.actions'
import { useDNADocumentVersionActions } from 'src/components/DNA/Document/DNADocumentVersion.actions'
import { withDNAFolderNav, useDNAFolderNav } from 'src/components/DNA/Folder/Nav/DNAFolderNav'
import { EmptyVariant } from 'src/components/DNA/Empty/DNAEmpty'
import DNAGrid from 'src/components/DNA/Grid/DNAGrid'
import { useAppSettings } from 'src/state/context/AppSettings'
import { ROUTES } from 'src/router/routes'
import useScreenNav from 'src/components/DNA/hooks/useScreenNav'
import truncate from 'lodash/truncate'
import DNARouteTabs from 'src/components/DNA/Navigation/DNARouteTabs/DNARouteTabs'
import DocumentsUpdates from './DocumentsUpdates'
import { useDispatch } from 'src/state/redux'
import { infoPanelActions } from 'src/state/redux/slice/infoPanel'
import { sharedFolderActions, useFetchSharedFolders } from 'src/state/redux/slice/sharedFolder'
import MyUploads from './MyUploads'
import ArchivedFolders from './ArchivedFolders'
import { useDNADocumentActions } from 'src/components/DNA/Document/DNADocument.actions'
import { getRootFolderORM } from 'src/state/redux/selector/folder'
import { isCustomDeckORM, isDocumentORM, isFolderORM } from 'src/types/typeguards'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import { FolderVariants } from './constants'
import FolderInfoHeader from './FolderInfoHeader'

export const S = StyleSheet.create({
  tab: {
    paddingHorizontal: 12,
    paddingVertical: 20,
  },
  gridViewContainer: {
    padding: 16,
  },
  tabContainer: {
    backgroundColor: 'white',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colors['color-gray-100'],
  },
  header: {
    backgroundColor: 'white',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderColor: colors['color-gray-100'],
    padding: 16,
  },
})

export const useDNAMyFoldersSharedResources = () => {
  const dispatch = useDispatch()
  const { go } = useScreenNav()
  const { displayMode, displayModeIconName, toggleDisplayMode } = useDisplayMode()
  const folderActions = useDNAFolderActions()
  const documentVersionActions = useDNADocumentVersionActions()
  const orientation = useOrientationChange()
  const route = useCurrentPage({ exact: false })
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const isArchivedFoldersRoute = !!route?.PATH.includes('archived_folders')
  const sharedFoldersTab = !!route?.PATH.includes('shared_folders')
  const folderNav = useDNAFolderNav()
  const documentActions = useDNADocumentActions()

  let folderNavURLPath = ROUTES.FOLDERS.PATH

  if (isPublisherRoute && folderNav.displaySharedFolders) {
    folderNavURLPath = ROUTES.PUBLISHER_SHARED_FOLDERS.PATH
  } else if (isPublisherRoute && isArchivedFoldersRoute) {
    folderNavURLPath = ROUTES.PUBLISHER_ARCHIVED_FOLDERS.PATH
  } else if (isPublisherRoute) {
    folderNavURLPath = ROUTES.PUBLISHER_FOLDERS.PATH
  } else if (isArchivedFoldersRoute) {
    folderNavURLPath = ROUTES.ARCHIVED_FOLDERS.PATH
  } else if (folderNav.displaySharedFolders) {
    folderNavURLPath = ROUTES.SHARED_FOLDERS.PATH
  }

  const folderNavURL = useFolderNavURL(folderNavURLPath)

  const activeItems = isPublisherRoute
    ? folderNav.currentItems
    : folderNav.currentItems.filter(
      item => (
        isDocumentVersionORM(item.relations.itemORM)
          ? item.relations.itemORM.model.status !== 'NOT_PUBLISHED'
          : true
      ),
    )

  const { deviceMode } = useAppSettings()
  const isTablet = deviceMode === 'tablet'

  const onPress = (selectedItemORM: FolderItemORM | FolderORM | DocumentORM) => {
    /** Folders */
    if (isFolderORM(selectedItemORM)) {
      const {
        model:{ id:selectedFolderID },
        relations:{ parentFolderORM },
        meta:{ hasExternalDependencies },
      } = selectedItemORM
      const { id:rootFolderID } = {
        ...(parentFolderORM
          ? getRootFolderORM(parentFolderORM)
          : selectedItemORM)?.model,
      }

      rootFolderID && hasExternalDependencies &&
        dispatch(sharedFolderActions.getFolderExternalDependencies({
          folderId: selectedFolderID,
          parentFolderId: rootFolderID,
        }))

      folderNavURL.pushFolder(selectedItemORM)

    /** Documents */
    } else if (isDocumentORM(selectedItemORM)) {
      const { conversionStatus } = selectedItemORM.relations.version.latestUsableDocumentVersionORM.model
      conversionStatus === 'PROCESSED' && documentActions.preview(selectedItemORM)();

    /** FolderItems (DocumentVersionORM | FolderORM | CustomDeckORM) */
    } else {
      const { itemORM:relatedItemORM } = selectedItemORM.relations
      if (isDocumentVersionORM(relatedItemORM)) {
        documentVersionActions.preview(relatedItemORM, selectedItemORM)()
      } else if (isCustomDeckORM(relatedItemORM)) {
        const { customDeckGroups } = relatedItemORM.meta
        for (const { model:{ visible }, pages } of customDeckGroups) {
          if (visible && pages[0]) {
            const { documentVersionORM } = pages[0]
            documentVersionActions.preview(documentVersionORM, selectedItemORM)()
            break
          }
        }
      }
    }
  }
  const createOnPress = () => {
    folderNav.isCurrentlyNested
      ? folderActions.createSubFolder(folderNav.folderStack.slice(-1)[0])()
      : folderActions.createFolder((undefined as any) as FolderORM)()
  }

  const returnToRoot = () => {
    go(ROUTES.FOLDERS)
  }

  /** TODO: This should be updated to a reusable component that generates breadcrumbs which can be utilized in both web and PWA.
   * Currently, it only serves the "tablet" variant and is using some outdated truncation techniques
   */
  const getBreadCrumb = (idx, truncateText = false, hideText = false) => {
    const notCurrentFolder = folderNav.folderStack.length - 1 !== idx
    const folderORM = folderNav.folderStack[idx]
    if (!folderORM) return null
    const { name } = { ...folderORM.model }
    return (
      <DNABox appearance="row" alignY="center">
        {notCurrentFolder &&
          <DNAButton
            appearance="ghost"
            status="tertiary"
            size="lg"
            iconLeft="caret-left"
            onPress={() => folderNavURL.popToFolder(folderORM)}
          />
        }
        <Iffy is={!hideText}>
          <DNABox>
            {truncateText
              ? isTablet
                ? <DNAText>
                  {truncate(name, { length: orientation === 'LANDSCAPE' ? 50 : 30 })}
                </DNAText>
                : null
              : <DNAText>
                {name}
              </DNAText>
            }
          </DNABox>
        </Iffy>
      </DNABox>)
  }

  const launchSlideBuilder = () => {
    /** This is taken from the createOnPress pattern above but is a
     * bit of a hack. Need to revisit this and properly implement */
    const currentFolder = folderNav.folderStack.slice(-1)[0]
    folderActions.createCustomPresentation(currentFolder)()
  }
  const { isOnline } = useAppSettings()
  let emptyVariantType: EmptyVariant;
  if (folderNav.isFolderListLoaded) {
    if (isArchivedFoldersRoute) {
      emptyVariantType = folderNav.isCurrentlyNested ? EmptyVariant.FolderGridEmpty : EmptyVariant.ArchivedFolderEmpty
    } else if (sharedFoldersTab) {
      if (!isOnline) {
        emptyVariantType = EmptyVariant.FolderListSharedOffline
      } else {
        emptyVariantType = EmptyVariant.FolderSharedList
      }
    } else {
      emptyVariantType = (folderNav.isCurrentlyNested ? EmptyVariant.FolderGridEmpty : EmptyVariant.FolderListCreate)
    }
  } else {
    emptyVariantType = (isOnline ? EmptyVariant.FolderListLoading : EmptyVariant.FolderListSharedOffline)
  }

  /** Inform the global info panel that we've navigated into a folder */
  useEffect(() => {
    const currentItem = folderNav.folderStack.slice(-1)[0];
    dispatch(infoPanelActions.setActiveItem(currentItem))
  }, [folderNav])

  return {
    folderActions,
    displayMode,
    displayModeIconName,
    toggleDisplayMode,
    folderNav,
    folderNavURL,
    activeItems,
    onPress,
    createOnPress,
    returnToRoot,
    getBreadCrumb,
    emptyVariantType,
    sharedFoldersTab,
    launchSlideBuilder,
  }
}

/**
 * Hook that returns the current tab name based on the route
 * @returns The name of the current tab
 */
const useCurrentTabName = () => {
  const route = useCurrentPage({ exact: false })

  if (route?.PATH.includes('shared_folders')) {
    return 'Shared With Me'
  }

  if (route?.PATH.includes('uploads')) {
    return ''
  }

  if (route?.PATH.includes('updates')) {
    return ''
  }

  if (route?.PATH.includes('archived_folders')) {
    return ''
  }

  // Default case - My Folders
  return 'My Folders'
}

export const DNAMyFoldersTabs: React.FC = () => {
  const route = useCurrentPage({ exact: false })
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const featureFlags = useFeatureFlags('enableSharedFolders', 'enableMyUploads')
  const currentTabName = useCurrentTabName()
  const folderNavURLPath = currentTabName === 'Shared With Me' ? ROUTES.SHARED_FOLDERS.PATH : ROUTES.FOLDERS.PATH
  const folderNav = useDNAFolderNav()
  const folderNavURL = useFolderNavURL(folderNavURLPath)

  return (
    <DNABox alignX="center">
      <DNABox fill alignX={isPublisherRoute ? 'stretch' : 'center'}>
        <DNABox
          fill
          style={{
            backgroundColor: isPublisherRoute ? colors['color-text-white'] : undefined,
          }}
        >
          <DNABox appearance="col" fill spacing="md">
            <Iffy is={currentTabName && folderNav.folderStack.length > 0}>
              <DNABox alignY="center" style={{ margin: 0 }}>
                <DNABreadcrumb
                  rootTitle={currentTabName}
                  folderStack={folderNav.folderStack}
                  onRootPress={() => folderNavURL.popToFolder()}
                  onFolderPress={(folderORM) => folderNavURL.popToFolder(folderORM)}
                  rootTestID="page-title"
                />
              </DNABox>
            </Iffy>
            <DNARouteTabs.Router>
              <Iffy is={isPublisherRoute}>
                <DNARouteTabs.Tabs withDivider style={S.tabContainer} hideTabs={true}>
                  <DNARouteTabs.Tab title="My Folders" path="/publisher_folders" style={S.tab} hasNavigationPaths>
                    <DNAMyFoldersDesktop />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Tab
                    title="Shared With Me"
                    path="/publisher_shared_folders"
                    style={[S.tab, !featureFlags.enableSharedFolders && { display: 'none' }]}
                    hasNavigationPaths
                  >
                    <DNAMyFoldersDesktop />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Tab title="My Updates" path="/publisher_folders/updates" style={S.tab}>
                    <DocumentsUpdates />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Tab
                    title="Archived Folders"
                    path="/publisher_archived_folders"
                    style={S.tab}
                    hasNavigationPaths
                  >
                    <ArchivedFolders />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Unmatched rewrite="/publisher_folders" />
                </DNARouteTabs.Tabs>
              </Iffy>
              <Iffy is={!isPublisherRoute}>
                <DNARouteTabs.Tabs withDivider style={S.tabContainer} hideTabs={true}>
                  <DNARouteTabs.Tab title="My Folders" path="/folders" style={S.tab} hasNavigationPaths>
                    <DNAMyFoldersDesktop />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Tab
                    title="Shared With Me"
                    path="/shared_folders"
                    style={[S.tab, !featureFlags.enableSharedFolders && { display: 'none' }]}
                    hasNavigationPaths
                  >
                    <DNAMyFoldersDesktop />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Tab
                    title="My Uploads"
                    path="/folders/uploads"
                    style={[S.tab, !featureFlags.enableMyUploads && { display: 'none' }]}
                  >
                    <MyUploads />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Tab title="My Updates" path="/folders/updates" style={S.tab}>
                    <DocumentsUpdates />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Tab title="Archived Folders" path="/archived_folders" style={S.tab} hasNavigationPaths>
                    <DNAMyFoldersDesktop />
                  </DNARouteTabs.Tab>
                  <DNARouteTabs.Unmatched rewrite="/folders" />
                </DNARouteTabs.Tabs>
              </Iffy>
            </DNARouteTabs.Router>
          </DNABox>
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

export const DNAMyFoldersDesktop: React.FC = () => {
  const {
    folderNav,
    displayMode,
    activeItems,
    onPress,
    emptyVariantType,
    sharedFoldersTab,
  } = useDNAMyFoldersSharedResources()
  const { setDisplaySharedFolders } = folderNav
  const route = useCurrentPage({ exact: false })
  const archivedFoldersTab = !!route?.PATH.includes('archived_folders');
  const gridListVariant = folderNav.isCurrentlyNested
    ? FolderVariants.DEFAULT
    : folderNav.displaySharedFolders
      ? FolderVariants.SHARED_WITH_ME_ROOT_FOLDER
      : FolderVariants.ROOT_FOLDER;

  useEffect(() => {
    if (sharedFoldersTab) setDisplaySharedFolders(sharedFoldersTab)
    else setDisplaySharedFolders(false);
  }, [sharedFoldersTab, archivedFoldersTab]);

  if (archivedFoldersTab) return <ArchivedFolders />

  return (
    <DNABox fill appearance="col">
      <FolderInfoHeader />

      {/* Content */}
      <Iffy is={displayMode === DisplayModes.grid}>
        <DNABox style={S.gridViewContainer}>
          <DNAGrid
            items={activeItems}
            onPress={onPress}
            emptyVariant={emptyVariantType}
          />
        </DNABox>
      </Iffy>
      <Iffy is={displayMode === DisplayModes.list}>
        <DNAFolderGridList
          variant={gridListVariant}
          items={activeItems}
          emptyVariant={emptyVariantType}
          onPress={onPress}
        />
      </Iffy>
    </DNABox>
  )
}

// [TODO]: We can potentially just render <Routes> within the above component
// - Try using useRouteMatch and then do a conditional route render if we don't
//   exceed the max depth
const NestedRoutes: React.FC = () => {
  useFolderNavURLInit()

  const { isOnline } = useAppSettings()
  const enableSharedFolders = useFeatureFlags('enableSharedFolders')
  const { fetchSharedFolders } = useFetchSharedFolders();

  useEffect(() => {
    if (enableSharedFolders && isOnline) {
      fetchSharedFolders();
    }
  }, [isOnline])

  return (
    <Switch>
      {/* Viewer */}
      <Route exact path="/folders" component={DNAMyFoldersTabs} />
      <Route exact path="/folders/:id" component={DNAMyFoldersTabs} />
      <Route exact path="/folders/:id/:idd" component={DNAMyFoldersTabs} />
      <Route exact path="/shared_folders" component={DNAMyFoldersTabs} />
      <Route exact path="/shared_folders/:id" component={DNAMyFoldersTabs} />
      <Route exact path="/shared_folders/:id/:idd" component={DNAMyFoldersTabs} />
      <Route exact path="/archived_folders" component={DNAMyFoldersTabs} />
      <Route exact path="/archived_folders/:id" component={DNAMyFoldersTabs} />
      <Route exact path="/archived_folders/:id/:idd" component={DNAMyFoldersTabs} />
      {/* publisher */}
      <Route exact path="/publisher_folders" component={DNAMyFoldersTabs} />
      <Route exact path="/publisher_folders/:id" component={DNAMyFoldersTabs} />
      <Route exact path="/publisher_folders/:id/:idd" component={DNAMyFoldersTabs} />
      <Route exact path="/publisher_shared_folders" component={DNAMyFoldersTabs} />
      <Route exact path="/publisher_shared_folders/:id" component={DNAMyFoldersTabs} />
      <Route exact path="/publisher_shared_folders/:id/:idd" component={DNAMyFoldersTabs} />
      <Route exact path="/publisher_archived_folders" component={DNAMyFoldersTabs} />
      <Route exact path="/publisher_archived_folders/:id" component={DNAMyFoldersTabs} />
      <Route exact path="/publisher_archived_folders/:id/:idd" component={DNAMyFoldersTabs} />
    </Switch>
  )
}

export default withDNAFolderNav(
  NestedRoutes,
  // TODO: Suggest updating this to filter shared dynamically based on selected tab
)
